import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, CircularProgress, InputAdornment, IconButton } from '@material-ui/core';
import { ErrorIcon, LocationIcon } from '../../../icons';

import { useNotify } from '../../../hooks/useNotify';
import { doSwaggerCall } from '../../../hooks/useSwaggerClient';
import AdminHeader from '../../../components/atom/AdminHeader';
import { useHistory } from 'react-router';
import FormSection from '../../../components/atom/FormSection';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import AutocompleteSelect from '../../../components/atom/AutocompleteSelect';
import { islandAutocomplete } from '../../../autocomplete';
import MapPinPicker from '../../../components/MapPinPicker';
import { formatPhone } from '../../../helpers/phoneUtils';

const HotelEditPage = () => {
    const { hotelId } = useParams();
    const isEdit = !!hotelId;

    const { notifyError, notifySuccess } = useNotify();
    const history = useHistory();
    const [hotel, setHotel] = useState({
        name: '',
        island_id: '',
        address: '',
        zip_code: '',
        hotel_type: '',
        hotel_phone: '',
        hotel_email: '',
        longitude: '',
        latitude: ''
    });
    const [loading, setLoading] = useState(isEdit);
    const [error, setError] = useState(null);
    const [geocoding, setGeocoding] = useState(false);

    const isValidLongitude = val => val === '' || (!isNaN(val) && val >= -180 && val <= 180);
    const isValidLatitude = val => val === '' || (!isNaN(val) && val >= -90 && val <= 90);
    const isValidEmail = val => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
    const longitudeError = hotel.longitude !== '' && !isValidLongitude(hotel.longitude);
    const latitudeError = hotel.latitude !== '' && !isValidLatitude(hotel.latitude);
    const emailError = hotel.hotel_email && !isValidEmail(hotel.hotel_email);
    const nameOverLimit = hotel.name && hotel.name.length > 80;
    const addressOverLimit = hotel.address && hotel.address.length > 255;
    const hasValidationError =
        !hotel.name ||
        !hotel.island_id ||
        !hotel.address ||
        longitudeError ||
        latitudeError ||
        emailError ||
        nameOverLimit ||
        addressOverLimit;

    const geocodeAddress = useCallback(() => {
        if (!hotel.address) {
            return;
        }
        setGeocoding(true);
        // Use Nominatim (OpenStreetMap) for geocoding
        const query = encodeURIComponent(hotel.address);
        fetch(`https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1`)
            .then(res => res.json())
            .then(data => {
                if (data && data.length > 0) {
                    setHotel(h => ({
                        ...h,
                        longitude: parseFloat(data[0].lon).toFixed(7),
                        latitude: parseFloat(data[0].lat).toFixed(7)
                    }));
                    notifySuccess('Address geocoded successfully');
                } else {
                    notifyError('Could not find coordinates for this address');
                }
            })
            .catch(() => {
                notifyError('Geocoding failed');
            })
            .finally(() => {
                setGeocoding(false);
            });
    }, [hotel.address, notifySuccess, notifyError]);

    useEffect(() => {
        if (!isEdit) {
            return;
        }

        setLoading(true);
        setError(null);

        doSwaggerCall('hotels', 'getHotelById', {
            hotelId: Number(hotelId)
        })
            .then(data => {
                setHotel(data);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setError(err);
                setLoading(false);
            });
    }, [isEdit, hotelId]);

    const modifyOrCreateHotel = () => {
        doSwaggerCall('hotels', isEdit ? 'updateHotel' : 'createHotel', {
            hotel: {
                name: hotel.name,
                island_id: hotel.island_id,
                address: hotel.address,
                zip_code: hotel.zip_code || null,
                hotel_type: hotel.hotel_type,
                hotel_phone: hotel.hotel_phone,
                hotel_email: hotel.hotel_email,
                longitude: parseFloat(hotel.longitude),
                latitude: parseFloat(hotel.latitude)
            },
            hotelId: isEdit ? Number(hotelId) : undefined
        })
            .then(newHotel => {
                setHotel(newHotel);
                notifySuccess(isEdit ? 'Hotel modified!' : 'Hotel created!');
                history.push('/admin/hotels');
            })
            .catch(err => {
                console.error(err);
                notifyError(isEdit ? 'Could not modify hotel!' : 'Could not create hotel!');
            });
    };

    if (error) {
        return (
            <Grid container justifyContent="center">
                <h1>{error}</h1>
            </Grid>
        );
    }

    if (loading) {
        return (
            <Grid container justifyContent="center">
                <CircularProgress />
            </Grid>
        );
    }

    return (
        <Grid className="bg-white activity-edit">
            <form
                onSubmit={ev => {
                    ev.preventDefault();
                }}
            >
                <Grid container spacing={2}>
                    <AdminHeader title={isEdit ? 'Edit Hotel' : 'New Hotel'} />

                    {!!error && (
                        <Grid item container spacing={2} xs={12} alignItems="center">
                            <Grid item>
                                <ErrorIcon color="error" />
                            </Grid>
                            <Grid item>
                                <span>{error.response.body.message}</span>
                            </Grid>
                        </Grid>
                    )}

                    <FormSection>
                        <Grid item container xs={6} spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Name"
                                    fullWidth
                                    required
                                    type="text"
                                    value={hotel.name}
                                    onChange={ev => setHotel({ ...hotel, name: ev.target.value })}
                                    inputProps={{ maxLength: 80 }}
                                    error={nameOverLimit}
                                    helperText={nameOverLimit ? 'Max 80 characters' : ''}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <AutocompleteSelect
                                    initCall={islandAutocomplete.init}
                                    autocompleteCall={islandAutocomplete.search}
                                    value={hotel.island_id}
                                    setValue={ev => setHotel({ ...hotel, island_id: ev })}
                                >
                                    <TextField label="Island" required />
                                </AutocompleteSelect>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Address"
                                    fullWidth
                                    required
                                    type="text"
                                    value={hotel.address}
                                    onChange={ev =>
                                        setHotel({ ...hotel, address: ev.target.value })
                                    }
                                    inputProps={{ maxLength: 255 }}
                                    error={addressOverLimit}
                                    helperText={addressOverLimit ? 'Max 255 characters' : ''}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={geocodeAddress}
                                                    disabled={geocoding || !hotel.address}
                                                    title="Lookup coordinates from address"
                                                    size="small"
                                                >
                                                    {geocoding ? (
                                                        <CircularProgress size={20} />
                                                    ) : (
                                                        <LocationIcon />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Zip Code"
                                    fullWidth
                                    type="text"
                                    value={hotel.zip_code ?? ''}
                                    onChange={ev =>
                                        setHotel({
                                            ...hotel,
                                            zip_code: ev.target.value
                                        })
                                    }
                                    inputProps={{ maxLength: 50 }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Hotel Type"
                                    fullWidth
                                    type="text"
                                    value={hotel.hotel_type}
                                    onChange={ev =>
                                        setHotel({
                                            ...hotel,
                                            hotel_type: ev.target.value
                                        })
                                    }
                                    inputProps={{ maxLength: 255 }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Hotel Phone"
                                    fullWidth
                                    type="tel"
                                    value={hotel.hotel_phone}
                                    onChange={ev =>
                                        setHotel({
                                            ...hotel,
                                            hotel_phone: ev.target.value
                                        })
                                    }
                                    onBlur={() => {
                                        if (hotel.hotel_phone) {
                                            const formatted = formatPhone(hotel.hotel_phone);
                                            if (formatted !== hotel.hotel_phone) {
                                                setHotel({
                                                    ...hotel,
                                                    hotel_phone: formatted
                                                });
                                            }
                                        }
                                    }}
                                    inputProps={{ maxLength: 300 }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Hotel Email"
                                    fullWidth
                                    type="text"
                                    value={hotel.hotel_email}
                                    onChange={ev =>
                                        setHotel({
                                            ...hotel,
                                            hotel_email: ev.target.value
                                        })
                                    }
                                    inputProps={{ maxLength: 300 }}
                                    error={emailError}
                                    helperText={emailError ? 'Invalid email format' : ''}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Longitude"
                                    fullWidth
                                    type="text"
                                    inputMode="decimal"
                                    value={hotel.longitude}
                                    onChange={ev =>
                                        setHotel({
                                            ...hotel,
                                            longitude: ev.target.value
                                        })
                                    }
                                    error={longitudeError}
                                    helperText={
                                        longitudeError ? 'Must be between -180 and 180' : ''
                                    }
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Latitude"
                                    fullWidth
                                    type="text"
                                    inputMode="decimal"
                                    value={hotel.latitude}
                                    onChange={ev =>
                                        setHotel({
                                            ...hotel,
                                            latitude: ev.target.value
                                        })
                                    }
                                    error={latitudeError}
                                    helperText={latitudeError ? 'Must be between -90 and 90' : ''}
                                />
                            </Grid>
                        </Grid>
                        <Grid item xs={6}>
                            <MapPinPicker
                                longitude={hotel.longitude}
                                latitude={hotel.latitude}
                                onChange={(lon, lat) =>
                                    setHotel({
                                        ...hotel,
                                        longitude: lon,
                                        latitude: lat
                                    })
                                }
                                height={300}
                            />
                        </Grid>
                    </FormSection>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            disabled={hasValidationError}
                            onClick={modifyOrCreateHotel}
                        >
                            Save
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => history.push('/admin/hotels')}
                        >
                            Back
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Grid>
    );
};

export default HotelEditPage;
